import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { CalendarCheck, Clock, DollarSign, ListChecks } from "lucide-react";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const statusColor = {
  pending: "bg-[#F4E9C9] text-[#8A6D1E]",
  confirmed: "bg-[#D9EAD9] text-[#3B7A57]",
  completed: "bg-[#E2E8E4] text-[#4A5D4E]",
  cancelled: "bg-[#F5DAD3] text-[#C0492B]",
};

export default function Admin() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/bookings/all").then((r) => setBookings(r.data)).catch(() => toast.error("Failed to load bookings."));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      toast.success(`Marked as ${status}.`);
      load();
    } catch { toast.error("Update failed."); }
  };

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const revenue = bookings.filter((b) => b.status !== "cancelled").reduce((s, b) => s + (b.price || 0), 0);
  const today = new Date().toISOString().slice(0, 10);
  const todayCount = bookings.filter((b) => b.date === today && b.status !== "cancelled").length;
  const pendingCount = bookings.filter((b) => b.status === "pending").length;

  const stats = [
    { icon: ListChecks, label: "Total bookings", value: bookings.length },
    { icon: Clock, label: "Pending", value: pendingCount },
    { icon: CalendarCheck, label: "Today", value: todayCount },
    { icon: DollarSign, label: "Pipeline", value: `$${revenue.toLocaleString()}` },
  ];

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <section className="bg-[#1A362D] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FA38C]">Admin Dashboard</span>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-white tracking-tight">Appointments control room</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((s) => (
            <div key={s.label} className="bg-white border border-[#E2E8E4] rounded-xl p-5" data-testid={`stat-${s.label.replace(/\s/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-2 text-[#8A9A8C]"><s.icon className="h-4 w-4" /><span className="text-xs uppercase tracking-wider">{s.label}</span></div>
              <p className="mt-2 font-heading text-3xl font-semibold text-[#1A362D]">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-semibold text-[#1A362D]">All bookings</h2>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 bg-white border-[#E2E8E4]" data-testid="admin-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-[#E2E8E4] rounded-xl overflow-hidden">
          <Table data-testid="admin-bookings-table">
            <TableHeader>
              <TableRow className="bg-[#F2F4F2]">
                <TableHead>Service</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Date / Time</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-[#8A9A8C]">No bookings found.</TableCell></TableRow>
              ) : filtered.map((b) => (
                <TableRow key={b.booking_id} data-testid={`admin-row-${b.booking_id}`}>
                  <TableCell>
                    <p className="font-medium text-[#1A362D]">{b.service_name}</p>
                    <p className="text-xs text-[#8A9A8C] capitalize">{b.category} · {b.recurring}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-[#1A362D]">{b.contact_name}</p>
                    <p className="text-xs text-[#8A9A8C]">{b.contact_phone}</p>
                  </TableCell>
                  <TableCell className="text-sm text-[#4A5D4E]">{b.date}<br /><span className="text-xs text-[#8A9A8C]">{b.time_slot}</span></TableCell>
                  <TableCell className="text-sm text-[#4A5D4E]">{b.address}, {b.zip_code}</TableCell>
                  <TableCell className="font-medium text-[#C47B57]">${b.price}</TableCell>
                  <TableCell>
                    <Select value={b.status} onValueChange={(v) => updateStatus(b.booking_id, v)}>
                      <SelectTrigger className={`w-32 border-0 ${statusColor[b.status]}`} data-testid={`status-${b.booking_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}
